import { useStoreProductArticles, useStoreStock } from '@gozoki/api'
import { Autocomplete, MenuItem, Paper, Select, TextField } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { z } from 'zod'
import SimpleDataGrid from '~/components/containers/SimpleDataGrid'
import { StoreDetailsLayout } from '~/components/routes/stores/StoreDetailsLayout'
import {
  articleStateFilter,
  articlesColDef,
  toArticleRow,
  type FilterArticleState,
} from '~/components/routes/stores/stocks/stockGrids'
import { STORE_TAB_PERMISSIONS } from '~/components/routes/stores/tabs'
import { withRoutePermissions } from '~/utils/rbac'
import { DataGridOverrides } from '~/utils/theme/overrides.theme'

export const Route = createFileRoute('/_authenticated/stores/$storeId/stocks/product/$productId')({
  // Même écran que l'onglet Stocks, resserré sur un produit : même droit de lecture.
  staticData: {
    permission: STORE_TAB_PERMISSIONS.stocks,
  },
  component: StoreProductArticlesPage,
  params: {
    parse: z.object({ productId: z.coerce.number(), storeId: z.coerce.number() }).parse,
  },
})
withRoutePermissions(Route)

function StoreProductArticlesPage() {
  const navigate = Route.useNavigate()
  const { storeId, productId } = Route.useParams()

  const [articleState, setArticleState] = useState<FilterArticleState>('all')

  const stock = useStoreStock(storeId)
  const articles = useStoreProductArticles(storeId, productId)

  const productOptions = useMemo(
    () => (stock.data?.rows ?? []).map((row) => ({ label: row.label, value: row.id })),
    [stock.data],
  )
  const selectedProduct = useMemo(
    () => productOptions.find((option) => option.value === productId) ?? null,
    [productOptions, productId],
  )

  const articlesRows = useMemo(() => {
    if (articles.data === undefined) {
      return []
    }
    return articles.data.filter((a) => articleStateFilter(a, articleState)).map(toArticleRow)
  }, [articles.data, articleState])

  return (
    <StoreDetailsLayout
      storeId={storeId}
      tab="stocks"
      titlePicker={
        <Autocomplete
          options={productOptions}
          value={selectedProduct}
          loading={stock.isLoading}
          // Élargi à `boolean` : littéral, MUI exige alors une valeur non nulle, que la liste des
          // options ne garantit pas tant qu'elle charge.
          disableClearable={true as boolean}
          size="small"
          sx={{ flexGrow: 1, maxWidth: 420, minWidth: 260 }}
          isOptionEqualToValue={(option, current) => option.value === current.value}
          onChange={(_, option) => {
            if (option) {
              navigate({
                to: '/stores/$storeId/stocks/product/$productId',
                params: { productId: option.value, storeId },
              })
            }
          }}
          renderInput={(params) => <TextField {...params} label="Produit" />}
        />
      }
      // Le produit consulté n'existe pas forcément dans la boutique choisie : on revient au stock.
      onStoreChange={(target) =>
        navigate({ to: '/stores/$storeId/stocks', params: { storeId: target } })
      }
      onBack={() => navigate({ to: '/stores/$storeId/stocks', params: { storeId } })}
    >
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
          <Select
            value={articleState}
            onChange={(e) => setArticleState(e.target.value as FilterArticleState)}
          >
            <MenuItem value="all">Tout</MenuItem>
            <MenuItem value="antigaspi">Anti-gaspi</MenuItem>
            <MenuItem value="expired">Expiré</MenuItem>
            <MenuItem value="normal">Normal</MenuItem>
          </Select>
          <div style={{ flexGrow: 1 }} />
        </div>
        <SimpleDataGrid
          columns={articlesColDef}
          rows={articlesRows}
          getRowId={(row) => row.id}
          loading={articles.isLoading}
          checkboxSelection={false}
          sx={DataGridOverrides.wrapHeaderNames}
        />
      </Paper>
    </StoreDetailsLayout>
  )
}
